import React, { useState } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink, Trash2 } from 'lucide-react';
import { ProjectAPI, TaskAPI, RunAPI, DocsAPI } from '../lib/api';
import { Dialog } from '../components/ui/Dialog';
import { Card, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Textarea } from '../components/ui/Input';
import { Table, Thead, Tbody, Th, Tr, Td } from '../components/ui/Table';
import { StatusBadge } from '../components/StatusBadge';
import { MarkdownView } from '../components/MarkdownView';
import { Skeleton } from '../components/ui/Skeleton';
import { TopBar } from '../components/TopBar';
import { useToast } from '../components/ui/Toast';
import { formatRelative, formatDate, formatDuration } from '../lib/format';

export function ProjectDetailPage() {
  const { slug } = useParams({ strict: false });
  const navigate = useNavigate();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [notes, setNotes] = useState('');
  const [notesDirty, setNotesDirty] = useState(false);
  const [docPath, setDocPath] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data: project, isLoading } = useQuery({
    queryKey: ['project', slug],
    queryFn: () => ProjectAPI.get(slug),
    onSuccess: (p: any) => { setNotes(p.notes ?? ''); },
  } as any);

  const { data: tasksData } = useQuery({
    queryKey: ['tasks', { project: slug }],
    queryFn: () => TaskAPI.list({ project: slug }),
  });

  const { data: runsData } = useQuery({
    queryKey: ['runs', { project: slug, limit: '10' }],
    queryFn: () => RunAPI.list({ project: slug, limit: '10' }),
  });

  const { data: docContent } = useQuery({
    queryKey: ['doc', docPath],
    queryFn: () => DocsAPI.file(docPath!),
    enabled: !!docPath,
  });

  const saveNotesMut = useMutation({
    mutationFn: () => ProjectAPI.update(slug, { notes }),
    onSuccess: () => { toast('Нотатки збережено', 'success'); setNotesDirty(false); },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const deleteMut = useMutation({
    mutationFn: () => ProjectAPI.delete(slug),
    onSuccess: () => {
      toast('Проєкт архівовано', 'success');
      navigate({ to: '/projects' });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const tasks: any[] = tasksData?.items ?? tasksData ?? [];
  const runs: any[] = runsData?.items ?? runsData ?? [];

  return (
    <div>
      <TopBar
        title={isLoading ? 'Завантаження...' : (project?.name ?? 'Проєкт')}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/projects' })}>
              <ArrowLeft size={14} /> Назад
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmDelete(true)}
              className="text-tg-error hover:text-tg-error"
            >
              <Trash2 size={14} />
            </Button>
          </div>
        }
      />
      <div className="p-6 space-y-4">
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-full" />
          </div>
        ) : project ? (
          <>
            <Card>
              <div className="flex items-center gap-3 mb-3">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{
                    background: project.status === 'active' ? 'var(--tg-success)' : 'var(--tg-text-secondary)'
                  }}
                />
                <h2 className="text-lg font-bold text-tg-text">{project.name}</h2>
                <Badge variant={project.status === 'active' ? 'success' : 'muted'}>
                  {project.status}
                </Badge>
              </div>
              {project.description && (
                <p className="text-sm text-tg-text-secondary mb-4">{project.description}</p>
              )}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {project.path && (
                  <div>
                    <span className="text-tg-text-secondary">Шлях: </span>
                    <code className="text-tg-text">{project.path}</code>
                  </div>
                )}
                {project.port && (
                  <div>
                    <span className="text-tg-text-secondary">Порт: </span>
                    <span className="text-tg-text">{project.port}</span>
                  </div>
                )}
                {project.language && (
                  <div>
                    <span className="text-tg-text-secondary">Мова: </span>
                    <span className="text-tg-text">{project.language}</span>
                  </div>
                )}
                {project.serviceUnit && (
                  <div>
                    <span className="text-tg-text-secondary">Сервіс: </span>
                    <code className="text-tg-text">{project.serviceUnit}</code>
                  </div>
                )}
                {project.botUsername && (
                  <div>
                    <span className="text-tg-text-secondary">Бот: </span>
                    <a
                      href={`https://t.me/${project.botUsername}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-tg-accent flex items-center gap-1"
                    >
                      @{project.botUsername} <ExternalLink size={12} />
                    </a>
                  </div>
                )}
              </div>
              {project.domains?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {project.domains.map((d: any) => (
                    <Badge key={d.domain || d} variant={d.ssl ? 'success' : 'muted'}>
                      {d.domain || d}
                    </Badge>
                  ))}
                </div>
              )}
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Cron задачі</CardTitle>
                <Button
                  size="sm"
                  onClick={() => navigate({ to: '/tasks/new' })}
                >
                  + Нова
                </Button>
              </CardHeader>
              {tasks.length > 0 ? (
                <Table>
                  <Thead>
                    <Tr>
                      <Th>Назва</Th>
                      <Th>Розклад</Th>
                      <Th>Статус</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {tasks.map((t: any) => (
                      <Tr
                        key={t.id}
                        onClick={() => navigate({ to: '/tasks/$taskId', params: { taskId: String(t.id) } })}
                      >
                        <Td className="font-medium">{t.name}</Td>
                        <Td className="font-mono text-xs text-tg-accent">{t.schedule}</Td>
                        <Td>
                          {t.lastRunStatus ? <StatusBadge status={t.lastRunStatus} /> : '—'}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              ) : (
                <p className="text-sm text-tg-text-secondary">Задачі відсутні</p>
              )}
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Останні запуски</CardTitle>
              </CardHeader>
              {runs.length > 0 ? (
                <Table>
                  <Thead>
                    <Tr>
                      <Th>Задача</Th>
                      <Th>Старт</Th>
                      <Th>Тривалість</Th>
                      <Th>Статус</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {runs.map((r: any) => (
                      <Tr key={r.id}>
                        <Td>{r.taskName || r.task?.name || '—'}</Td>
                        <Td className="text-xs text-tg-text-secondary">{formatDate(r.startedAt)}</Td>
                        <Td className="text-xs text-tg-text-secondary">
                          {r.durationMs != null ? formatDuration(r.durationMs) : '—'}
                        </Td>
                        <Td><StatusBadge status={r.status} /></Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              ) : (
                <p className="text-sm text-tg-text-secondary">Запуски відсутні</p>
              )}
            </Card>

            {project.readmePath && (
              <Card>
                <CardHeader>
                  <CardTitle>README</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDocPath(docPath ? null : project.readmePath)}
                  >
                    {docPath ? 'Сховати' : 'Показати'}
                  </Button>
                </CardHeader>
                {docPath && docContent && (
                  <MarkdownView content={docContent} />
                )}
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Нотатки</CardTitle>
                {notesDirty && (
                  <Button size="sm" loading={saveNotesMut.isPending} onClick={() => saveNotesMut.mutate()}>
                    Зберегти
                  </Button>
                )}
              </CardHeader>
              <Textarea
                value={notes}
                onChange={(e) => { setNotes(e.target.value); setNotesDirty(true); }}
                placeholder="Нотатки у форматі Markdown..."
                rows={6}
              />
            </Card>
          </>
        ) : (
          <p className="text-tg-text-secondary">Проєкт не знайдено</p>
        )}
      </div>

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Видалити проєкт?"
      >
        <p className="text-sm text-tg-text-secondary mb-4">Він буде архівований.</p>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" size="sm" onClick={() => setConfirmDelete(false)}>
            Скасувати
          </Button>
          <Button
            size="sm"
            loading={deleteMut.isPending}
            onClick={() => { setConfirmDelete(false); deleteMut.mutate(); }}
            className="bg-tg-error text-white hover:opacity-90"
          >
            Видалити
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
